import { useNavigate, useParams } from "react-router-dom";
import { useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext } from "components/GlobalContext";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import IProduct, { IProductCreateOrUpdate, IProductInitResult } from "models/sql/IProduct";
import ProductService from "services/ProductService";
import MarkdownEditor from "components/MarkdownEditor";
import { useBeforeUnload } from "utilities/UIUtilities";
import DetailFooter from "components/DetailFooter";

interface IProductsDetailProps {
    baseData?: IProductInitResult;
}

const ProductsDetail = (props: IProductsDetailProps) => {
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const { id }: any = useParams();
    const [product, setProduct] = useState<IProduct | null>({
        name: "",
        partNumber: "",
        productId: -1,
        serialPrefix: "",
        description: "",
        equipment: undefined,
        imagePath: "",
        postRegistrationNotificationDays: 0,
        postRegistrationNotificationMessage: "",
        postRegistrationPath: "",
        cost: 0,
        listPrice: 0,
        notes: "",
        avgSellPrice: 0,
        productType: "",
        publish: false,
        qtyOnOrder: 0,
        shortDescription: "",
        infoUrl: "",
        quotable: false,
        warrantyMonths: 0
    });
    const navigate = useNavigate();
    const [hasFormDataChanged, setHasFormDataChanged] = useState(false);
    const productTypes = ["Equipment", "Consumable"];

    useBeforeUnload(
        hasFormDataChanged,
        "You have unsaved changes, please save them before leaving the page.");

    useEffect(
        () => {
            (async () => {
                if (id !== undefined) {
                    globalContext.setLoading(true);

                    const accessToken = await globalContext.getAccessTokenAsync();
                    const result = await ProductService.GetAsync(accessToken, id);

                    if (result.record) {
                        setProduct(result.record);
                    }
                    else {
                        setProduct(null);
                        globalContext.showErrorMessage("Unable to load Product");
                    }

                    globalContext.setLoading(false);
                }
            })();
        }, [id]);

    const deleteAsync = async () => {
        if (product && product.productId && window.confirm("Are you SURE you want to delete this?")) {
            globalContext.setLoading(true);
            const accessToken = await globalContext.getAccessTokenAsync();
            await ProductService.DeleteAsync(accessToken, product.productId);
            globalContext.setLoading(false);
            navigate("/Products");
        }
    };

    const submitFormAsync = async () => {
        if (product) {
            globalContext.setLoading(true);
            const accessToken = await globalContext.getAccessTokenAsync();

            const data: IProductCreateOrUpdate = {
                name: product.name,
                partNumber: product.partNumber,
                productId: product.productId,
                serialPrefix: product.serialPrefix,
                description: product.description,
                notes: product.notes,
                imagePath: product.imagePath,
                postRegistrationNotificationDays: product.postRegistrationNotificationDays,
                postRegistrationNotificationMessage: product.postRegistrationNotificationMessage,
                postRegistrationPath: product.postRegistrationPath,
                cost: product.cost,
                listPrice: product.listPrice,
                avgSellPrice: product.avgSellPrice,
                productType: product.productType,
                publish: product.publish,
                qtyOnOrder: product.qtyOnOrder,
                infoUrl: product.infoUrl,
                shortDescription: product.shortDescription,
                quotable: product.quotable,
                warrantyMonths: product.warrantyMonths
            };

            await ProductService.CreateOrUpdateAsync(accessToken, data);
            globalContext.setLoading(false);
            setHasFormDataChanged(false);

            if (data.productId === -1) {
                navigate(-1);
            }
        }
    };

    if (product) {
        return (
            <>
                <Row>
                    <Col>
                        <Card className="mb-2 mt-2">
                            <Card.Body>
                                <Card.Title>
                                    {
                                        id !== undefined &&
                                        <strong>{product.name} ({product.partNumber})</strong>
                                    }
                                    {
                                        id === undefined &&
                                        <strong>New Product</strong>
                                    }
                                </Card.Title>
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Name</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setProduct({ ...product, name: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={product.name}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Part Number</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setProduct({ ...product, partNumber: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={product.partNumber}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Serial Prefix</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setProduct({ ...product, serialPrefix: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={product.serialPrefix}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Image Path</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setProduct({ ...product, imagePath: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={product.imagePath}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Quantity On Order</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setProduct({ ...product, qtyOnOrder: parseInt(e.target.value || "0") });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={product.qtyOnOrder || 0}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Average Sell Price</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setProduct({ ...product, avgSellPrice: parseFloat(e.target.value) });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={product.avgSellPrice}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>List Price</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setProduct({ ...product, listPrice: parseFloat(e.target.value) });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={product.listPrice}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Cost</Form.Label>
                                            <Form.Control
                                                type="text"
                                                onChange={
                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setProduct({ ...product, cost: parseFloat(e.target.value) });
                                                        setHasFormDataChanged(true);
                                                    }
                                                }
                                                value={product.cost}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Product Type</Form.Label>
                                            <Form.Select
                                                onChange={async (e) => {
                                                    setProduct({ ...product, productType: e.target.value });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={product.productType}>
                                                <option key={"empty"} value={""}>-</option>
                                                {
                                                    productTypes.map((s, index) => <option key={s} value={s}>{s}</option>)
                                                }
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Publish</Form.Label>
                                            <Form.Select
                                                onChange={async (e) => {
                                                    setProduct({ ...product, publish: e.target.value == "true" });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={product.publish ? "true" : "false"}>
                                                <option key={"empty"} value={""}>-</option>
                                                {
                                                    ["true", "false"].map((s, index) => <option key={s} value={s}>{s}</option>)
                                                }
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Quotable</Form.Label>
                                            <Form.Select
                                                onChange={async (e) => {
                                                    setProduct({ ...product, quotable: e.target.value == "true" });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={product.quotable ? "true" : "false"}>
                                                <option key={"empty"} value={""}>-</option>
                                                {
                                                    ["true", "false"].map((s, index) => <option key={s} value={s}>{s}</option>)
                                                }
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Warranty Months</Form.Label>
                                            <Form.Control
                                                type="text"
                                                onChange={
                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setProduct({ ...product, warrantyMonths: parseInt(e.target.value) });
                                                        setHasFormDataChanged(true);
                                                    }
                                                }
                                                value={product.warrantyMonths}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Post Registration Notification Days</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setProduct({ ...product, postRegistrationNotificationDays: parseInt(e.target.value || "0") });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={product.postRegistrationNotificationDays || 0}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Post Registration Path</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setProduct({ ...product, postRegistrationPath: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={product.postRegistrationPath}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Post Registration Notification Message</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={4}
                                                onChange={
                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                        // @ts-ignore
                                                        setProduct({ ...product, postRegistrationNotificationMessage: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }
                                                }
                                                value={(product?.postRegistrationNotificationMessage || "")}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Informational Url</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setProduct({ ...product, infoUrl: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={product.infoUrl}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Description</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={4}
                                                onChange={
                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                        // @ts-ignore
                                                        setProduct({ ...product, description: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }
                                                }
                                                value={(product?.description || "")}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Short Description</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={4}
                                                onChange={
                                                    (e: React.ChangeEvent<HTMLInputElement>) => {
                                                        // @ts-ignore
                                                        setProduct({ ...product, shortDescription: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }
                                                }
                                                value={(product?.shortDescription || "")}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {
                                    globalContext.roles.some(r => r === "Web.Admin") &&
                                    <Row>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Admin Notes</Form.Label>
                                            <MarkdownEditor
                                                noun="Note"
                                                onChange={newVal => {
                                                    setProduct({ ...product, notes: newVal });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={product.notes || ""}
                                            />
                                        </Form.Group>
                                    </Row>
                                }
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {
                    globalContext.roles.some(r => r === "Web.Admin") &&
                    <DetailFooter
                        hasFormDataChanged={hasFormDataChanged}
                        deleteAsync={deleteAsync}
                        submitFormAsync={submitFormAsync}
                    />
                }
            </>
        );
    }
    else {
        return (
            <>
                <h1>There was an issue retreiving the Product, please try again.</h1>
            </>
        );
    }
}

export default ProductsDetail;
